import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Box, IconButton as MuiIconButton, styled, TableCell, Tooltip, TableRow } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useForm, type DefaultValues, type FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IconButton } from '../../atoms';
import { ReactComponent as ConfirmIcon } from '../../../assets/icons/check.svg';
import { ReactComponent as CloseIcon } from '../../../assets/icons/x-mark.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/pencil_acc.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/trash.svg';
import { ReactComponent as SaveErrorIcon } from '../../../assets/icons/error-icon.svg';
import { ConfirmationCentered } from '../ConfirmationDialog/ConfirmationDialog';
import { EditableTableExpansion } from './EditableTableExpansion';
import type { EditableColumnDef, FieldChangeConfirm, RowEditContext } from './types';

const StyledRow = styled(TableRow, {
    shouldForwardProp: (prop) => prop !== '$rowHeight' && prop !== '$tinted' && prop !== '$editing' && prop !== '$error'
})<{ $rowHeight?: string; $tinted?: boolean; $editing?: boolean; $error?: boolean }>(
    ({ theme, $rowHeight, $tinted, $editing, $error }) => {
        const editingBorderColor = $error ? theme.palette.semantic.bd.error : theme.palette.semantic.int.selected;
        return {
            height: $rowHeight || '56px',
            ...($tinted && {
                backgroundColor: theme.palette?.primary[25],
                '& > .MuiTableCell-root': {
                    borderTop: `1px solid ${theme.palette.semantic.bd?.subtle}`,
                    borderBottom: `1px solid ${theme.palette.semantic.bd?.subtle}`
                },
                '& > .MuiTableCell-root:first-of-type': {
                    borderLeft: `1px solid ${theme.palette.semantic.bd.subtle}`
                }
            }),
            ...($editing && {
                '& > .MuiTableCell-root': {
                    borderTop: `1px solid ${editingBorderColor}`,
                    borderBottom: `1px solid ${editingBorderColor}`
                },
                '& > .MuiTableCell-root:first-of-type': {
                    borderLeft: `1px solid ${editingBorderColor}`
                },
                '& > .MuiTableCell-root:last-of-type': {
                    borderRight: `1px solid ${editingBorderColor}`
                },
                '&:last-of-type > .MuiTableCell-root:first-of-type': {
                    borderBottomLeftRadius: '8px'
                },
                '&:last-of-type > .MuiTableCell-root:last-of-type': {
                    borderBottomRightRadius: '8px'
                }
            })
        };
    }
);

const ExpandableCellContent = styled('span')(({ theme }) => ({
    cursor: 'pointer',
    color: theme.palette.primary.main,
    textDecoration: 'underline',
    '&:hover': { opacity: 0.8 }
}));

interface EditableTableRowEditProps<T extends FieldValues> {
    row: T;
    columns: EditableColumnDef<T>[];
    /**
     * `'edit'` (default) renders input cells with save/cancel icons.
     * `'view'` renders read-only cells with edit/delete icons but keeps the form mounted
     * so the expansion can still bind to the same react-hook-form draft.
     */
    mode?: 'view' | 'edit';
    // eslint-disable-next-line no-unused-vars
    onSave: (values: T) => void | Promise<void>;
    onCancel: () => void;
    /** When false, the cancel (X) icon is hidden while editing/adding. Defaults to true. */
    showCancel?: boolean;
    onStartEdit?: () => void;
    onDelete?: () => void;
    onToggleExpand?: () => void;
    // eslint-disable-next-line no-unused-vars
    onDirtyChange?: (isDirty: boolean) => void;
    showActions: boolean;
    expandable?: boolean;
    actionsColumnWidth?: string;
    rowHeight?: string;
    validationMode?: 'onChange' | 'onBlur' | 'onSubmit' | 'all';
    ariaLabels?: { edit?: string; delete?: string; save?: string; cancel?: string };
    // eslint-disable-next-line no-unused-vars
    renderExpansion?: (ctx: RowEditContext<T>) => React.ReactNode;
    expansionColSpan?: number;
    isLast?: boolean;
    saveError?: boolean;
    onClearSaveError?: () => void;
    confirmFieldChange?: FieldChangeConfirm<T>;
}

export const EditableTableRowEdit = <T extends FieldValues>({
    row,
    columns,
    mode = 'edit',
    onSave,
    onCancel,
    showCancel = true,
    onStartEdit,
    onDelete,
    onToggleExpand,
    onDirtyChange,
    showActions,
    expandable = false,
    actionsColumnWidth = '120px',
    rowHeight,
    validationMode = 'onChange',
    ariaLabels,
    renderExpansion,
    expansionColSpan,
    isLast = false,
    saveError = false,
    onClearSaveError,
    confirmFieldChange
}: EditableTableRowEditProps<T>) => {
    const { t } = useTranslation();
    const {
        control,
        register,
        handleSubmit,
        watch,
        setValue,
        getValues,
        trigger,
        clearErrors,
        setError,
        formState: { errors, isValid, isSubmitting, isDirty }
    } = useForm<T>({
        defaultValues: row as DefaultValues<T>,
        mode: validationMode
    });

    useEffect(() => {
        onDirtyChange?.(isDirty);
    }, [isDirty]);

    useEffect(() => {
        return () => {
            onDirtyChange?.(false);
        };
    }, []);

    // When a save error is being shown, reset it as soon as the user edits any field.
    useEffect(() => {
        if (!saveError) return;
        const subscription = watch(() => onClearSaveError?.());
        return () => subscription.unsubscribe();
    }, [saveError, watch, onClearSaveError]);

    // Opt-in guard: confirm before a monitored field change is applied on a qualifying row.
    const confirmRef = useRef(confirmFieldChange);
    confirmRef.current = confirmFieldChange;
    const rowRef = useRef(row);
    rowRef.current = row;
    const prevWatchedRef = useRef<Record<string, unknown>>({});
    // Once the user proceeds through the confirmation, don't ask again for the rest of this edit session.
    const confirmedOnceRef = useRef(false);
    const [pendingFieldConfirm, setPendingFieldConfirm] = useState<{ name: string; prevValue: unknown } | null>(null);

    // Re-arm the one-time confirmation each time the row (re)enters edit mode.
    useEffect(() => {
        if (mode === 'edit') confirmedOnceRef.current = false;
    }, [mode]);

    useEffect(() => {
        const cfg = confirmRef.current;
        if (!cfg?.watchFields?.length) return;
        const seed: Record<string, unknown> = {};
        cfg.watchFields.forEach((f) => {
            seed[f] = getValues(f as never);
        });
        prevWatchedRef.current = seed;
        const isSameValue = (a: unknown, b: unknown) => {
            if (a === b) return true;
            if (a == null || b == null) return false;
            const da = dayjs(a as never);
            const db = dayjs(b as never);
            return da.isValid() && db.isValid() && da.isSame(db);
        };
        const subscription = watch((_values, { name }) => {
            const current = confirmRef.current;
            if (!name || !current?.watchFields.includes(name)) return;
            const nextValue = getValues(name as never);
            const prevValue = prevWatchedRef.current[name];
            // Ignore partial input while the date is still being typed — only react once a complete
            // date has been entered (a fully typed year is 4 digits) so the confirmation doesn't fire early.
            if (nextValue != null) {
                const nextDate = dayjs(nextValue as never);
                if (!nextDate.isValid() || nextDate.year() < 1000) return;
            }
            if (isSameValue(nextValue, prevValue)) return;
            if (!confirmedOnceRef.current && current.shouldConfirm(rowRef.current)) {
                setPendingFieldConfirm({ name, prevValue });
            } else {
                prevWatchedRef.current[name] = nextValue;
            }
        });
        return () => subscription.unsubscribe();
    }, [watch, getValues]);

    const handleConfirmFieldChange = () => {
        confirmedOnceRef.current = true;
        if (pendingFieldConfirm) {
            prevWatchedRef.current[pendingFieldConfirm.name] = getValues(pendingFieldConfirm.name as never);
        }
        setPendingFieldConfirm(null);
    };

    const handleDeclineFieldChange = () => {
        if (pendingFieldConfirm) {
            // Restoring the prior value re-fires watch, but it now matches prevWatchedRef and is ignored.
            setValue(pendingFieldConfirm.name as never, pendingFieldConfirm.prevValue as never, {
                shouldValidate: true,
                shouldDirty: true
            });
        }
        setPendingFieldConfirm(null);
    };

    const ctx: RowEditContext<T> = {
        row,
        control,
        register,
        errors,
        watch,
        setValue,
        getValues,
        trigger,
        clearErrors,
        setError,
        isSubmitting
    };

    const onSubmit = handleSubmit(async (values) => {
        // Keep the row in edit mode when the save handler rejects (e.g. server validation error)
        // so the consumer can surface the error and let the user correct their input.
        try {
            await onSave(values as T);
        } catch (error) {
            void error;
        }
    });

    // Toggling expansion while the row is already expanded validates the form first —
    // RHF surfaces any errors (highlighting invalid fields) and we block the collapse until
    // they're resolved. When collapsed, the toggle passes through so the user can still open
    // the expansion to fill it in.
    const handleToggleExpandInternal = async () => {
        if (!onToggleExpand) return;
        if (renderExpansion) {
            const isValidForm = await trigger();
            if (!isValidForm) return;
        }
        onToggleExpand();
    };

    const isEdit = mode === 'edit';
    return (
        <>
            <StyledRow $rowHeight={rowHeight} $tinted={!!renderExpansion} $editing={isEdit} $error={saveError}>
                {columns.map((column, colIndex) => {
                    let cellContent: React.ReactNode;
                    if (isEdit && column.renderEdit) {
                        cellContent = column.renderEdit(ctx);
                    } else if (column.render) {
                        cellContent = column.render(row[column.key as keyof T], row);
                    } else {
                        cellContent = (row[column.key as keyof T] as React.ReactNode) ?? '-';
                    }

                    const showChevron = !isEdit && expandable && colIndex === 0;

                    if (column.expandable && onToggleExpand) {
                        return (
                            <TableCell
                                key={column.key}
                                align={column.align ?? 'left'}
                                sx={{
                                    width: column.width,
                                    verticalAlign: 'middle',
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    lineHeight: '20px'
                                }}
                            >
                                <ExpandableCellContent role="button" tabIndex={0} onClick={handleToggleExpandInternal}>
                                    {cellContent}
                                </ExpandableCellContent>
                            </TableCell>
                        );
                    }

                    return (
                        <TableCell
                            key={column.key}
                            align={column.align ?? 'left'}
                            sx={{
                                width: column.width,
                                verticalAlign: isEdit ? 'top' : 'middle',
                                padding: isEdit ? '8px' : undefined,
                                fontSize: isEdit ? undefined : '16px',
                                fontWeight: isEdit ? undefined : 500,
                                lineHeight: isEdit ? undefined : '20px'
                            }}
                        >
                            {showChevron ? (
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <MuiIconButton
                                        size="small"
                                        onClick={handleToggleExpandInternal}
                                        aria-label="Collapse row"
                                        sx={{ padding: '2px' }}
                                    >
                                        <KeyboardArrowDownIcon />
                                    </MuiIconButton>
                                    <Box>{cellContent}</Box>
                                </Box>
                            ) : (
                                cellContent
                            )}
                        </TableCell>
                    );
                })}
                {showActions && (
                    <TableCell
                        align="center"
                        sx={{
                            width: actionsColumnWidth,
                            verticalAlign: isEdit ? 'top' : 'middle',
                            paddingTop: isEdit ? '8px' : undefined
                        }}
                    >
                        <Box display="flex" gap={1} justifyContent="center" alignItems="center">
                            {isEdit ? (
                                <>
                                    {saveError && (
                                        <Box
                                            component="span"
                                            display="inline-flex"
                                            alignItems="center"
                                            sx={{ '& svg': { width: 20, height: 20 } }}
                                        >
                                            <SaveErrorIcon />
                                        </Box>
                                    )}
                                    <Tooltip title={!isValid ? t('COMPLETE_REQUIRED_FIELDS_ERROR') : ''}>
                                        <Box component="span" sx={{ display: 'inline-flex' }}>
                                            <IconButton
                                                iconName={<ConfirmIcon />}
                                                // isValid doesn't react to manual setError() calls (e.g. date-picker
                                                // range errors), so also gate on the live errors map. A save (API) error
                                                // also blocks retrying until the user edits a field and clears it.
                                                disabled={
                                                    !isValid ||
                                                    Object.keys(errors).length > 0 ||
                                                    isSubmitting ||
                                                    saveError
                                                }
                                                onClick={onSubmit}
                                                arialabel={ariaLabels?.save}
                                                sx={(theme) => ({
                                                    width: 32,
                                                    height: 32,
                                                    borderRadius: '8px',
                                                    backgroundColor: theme.palette.semantic.icon.brand,
                                                    '&:hover': {
                                                        backgroundColor: theme.palette.semantic.icon.brand
                                                    },
                                                    '&.Mui-disabled': {
                                                        backgroundColor: theme.palette.semantic.bg.raised,
                                                        '& svg path': { fill: theme.palette.semantic.bd.strong }
                                                    }
                                                })}
                                            />
                                        </Box>
                                    </Tooltip>
                                    {showCancel && (
                                        <IconButton
                                            iconName={<CloseIcon />}
                                            disabled={isSubmitting}
                                            onClick={onCancel}
                                            arialabel={ariaLabels?.cancel}
                                            sx={(theme) => ({
                                                width: 32,
                                                height: 32,
                                                borderRadius: '8px',
                                                backgroundColor: theme.palette.background.paper,
                                                border: `1px solid ${theme.palette.semantic.icon.brand}`,
                                                '&:hover': {
                                                    backgroundColor: theme.palette.semantic.int.hover
                                                }
                                            })}
                                        />
                                    )}
                                </>
                            ) : (
                                <>
                                    {onStartEdit && (
                                        <IconButton
                                            iconName={<EditIcon color="primary" />}
                                            bordered={false}
                                            onClick={onStartEdit}
                                            arialabel={ariaLabels?.edit}
                                            color="primary"
                                        />
                                    )}
                                    {onDelete && (
                                        <IconButton
                                            iconName={<DeleteIcon color="primary" />}
                                            bordered={false}
                                            onClick={onDelete}
                                            arialabel={ariaLabels?.delete}
                                            color="primary"
                                        />
                                    )}
                                </>
                            )}
                        </Box>
                    </TableCell>
                )}
            </StyledRow>
            {renderExpansion && (
                <EditableTableExpansion
                    open
                    colSpan={expansionColSpan ?? columns.length + (showActions ? 1 : 0)}
                    isLast={isLast}
                >
                    {renderExpansion(ctx)}
                </EditableTableExpansion>
            )}
            {pendingFieldConfirm && confirmFieldChange && (
                <ConfirmationCentered
                    open
                    title={confirmFieldChange.title}
                    content={confirmFieldChange.content}
                    confirmButtonLabel={confirmFieldChange.confirmLabel}
                    cancelButtonLabel={confirmFieldChange.cancelLabel}
                    onConfirmation={handleConfirmFieldChange}
                    onCancellation={handleDeclineFieldChange}
                    onCloseIconClick={handleDeclineFieldChange}
                />
            )}
        </>
    );
};
